// Psaní zprávy: Od (identity s tečkou účtu), Komu/Kopie/Skrytá jako čipy s napovídáním (lookup people), Předmět,
// editor HTML (contenteditable, bez knihoven) nebo prostý text, podpis podle identity, přílohy z počítače i ze Souborů,
// odeslání přes frontu s odkladem „Vzít zpět“, koncept v jádře (drafts) každých 30 s.
import { registerComponent } from '@core/views/factory';
import { action, core, upload, ApiError } from '@core/api';
import { h, clear, debounce } from '@core/dom';
import { t, tk, fmtSize, fmtDate, fmtTime } from '@core/i18n';
import { icon } from '@core/icons';
import { IwMenu, contextMenu, type MenuItem } from '@core/components/menu';
import { IwCommandBar, headButton, type BarItem } from '@core/components/commandbar';
import { useBar, renderBarHeader } from '@core/components/chrome';
import { prompt, confirm, dialog } from '@core/components/dialog';
import { toast } from '@core/components/toast';
import { pickFile } from '@core/shell/picker';
import { linkOptionsDialog } from '@core/components/publiclink';
import type { StorageItem, LookupHit } from '@core/contract';

interface Addr { name: string; email: string; invalid?: boolean }
interface Identity { id: string; account: string; accountName: string; color: string; name: string; email: string; replyTo: string | null; isDefault: boolean; signature: { html: string; text: string } | null; canSend: boolean; kind: 'own' | 'alias' | 'group' }
interface OrigAtt { id: string; name: string; mime: string; size: number; fileId: string | null }
interface Orig { id: string; from: string; fromEmail: string; toList: Addr[]; ccList: Addr[]; replyTo: string | null; subject: string; date: string | null; text: string; html: string | null; account: string; attachments: OrigAtt[] }
interface Draft { id: string; to: Addr[]; cc: Addr[]; subject: string; html: string | null; text: string; attachments: { fileId: string | null; name: string; size: number; mime: string }[] }
interface Payload { identities: Identity[]; identity: string | null; mode: string; orig: Orig | null; draft: Draft | null; to: string; subject: string; body: string; prefs: { format: string; sendDelay: number; signaturePos: string; quote: boolean }; userName: string }
interface Att { fileId: string; name: string; size: number; mime: string }

const bootPrefix = (JSON.parse(document.getElementById('iwan-boot')?.textContent ?? '{}') as { prefix?: string }).prefix ?? '';
const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c);

// Přetahování adres (jako IceWarp): čip jde přetáhnout na jiný řádek (Komu → Kopie) – přesun, nebo do jiného okna
// Nové zprávy – kopie (rozepsaná zpráva v původním okně o adresu nepřijde); Ctrl = kopie i v rámci okna.
const ADDR_MIME = 'application/x-iwan-address';
const openDraftKeys = new Set<string>(); // koncepty otevřené v oknech (nenabízet je jinému oknu)
const addrFields = new Map<string, (email: string) => void>(); // id pole → odebrat adresu (pro přesun)
let composeSeq = 0;

registerComponent('posta-compose', ({ desc, win, wm }) => {
  const composeId = 'c' + ++composeSeq;
  let fieldSeq = 0;
  const p = desc.payload as unknown as Payload;
  const ids = p.identities;
  let identity: Identity | undefined = ids.find((i) => i.id === p.identity) ?? ids[0];
  let html = p.prefs.format !== 'text';
  let signatureChoice: 'default' | 'none' | string = 'default';
  let signatureTouched = false;
  const atts: Att[] = [];
  let sentId: string | null = null;
  let draftMessage: string | null = p.draft?.id ?? null; // koncept ve složce Koncepty na IMAP (naše id zprávy)
  // koncept: odpověď / přeposlání / koncept ze složky mají klíč podle zprávy; nová zpráva vlastní klíč okna (dvě okna Nová
  // zpráva si nesmí přepisovat koncept) – rozepsanou novou zprávu z dřívějška okno jen nabídne (Pokračovat / Zahodit, vzor F3)
  const isNew = !p.draft && !p.orig;
  let draftKey = `posta/napsat/${p.draft ? 'draft:' + p.draft.id : p.orig ? p.mode + ':' + p.orig.id : 'new:' + Math.random().toString(36).slice(2, 10)}`;
  openDraftKeys.add(draftKey);

  // ---- adresy (čipy + napovídání)
  function addrField(label: string, initial: Addr[]): { el: HTMLElement; get: () => Addr[]; set: (a: Addr[]) => void; row: HTMLElement } {
    const list: Addr[] = [...initial];
    const chips = h('div', { class: 'mail-chips' });
    const input = h('input', { class: 'mail-addr-input', type: 'text', autocomplete: 'off', spellcheck: 'false', placeholder: t('posta.napsat.add_rcpt') }) as HTMLInputElement;
    const box = h('div', { class: 'mail-addr-box' }, chips, input);
    const row = h('div', { class: 'mail-compose-row' }, h('span', { class: 'mail-compose-label', onClick: () => input.focus() }, label), box); // ne <label>: klik na čip by aktivoval první tlačítko (křížek)
    const fieldId = composeId + ':' + ++fieldSeq;
    addrFields.set(fieldId, (email) => { const i = list.findIndex((a) => a.email === email); if (i >= 0) { list.splice(i, 1); render(); touch(); } });
    box.addEventListener('dragover', (e) => {
      if (!e.dataTransfer?.types.includes(ADDR_MIME)) return;
      e.preventDefault();
      e.stopPropagation(); // ne pole pro přílohy (celé okno)
      e.dataTransfer.dropEffect = e.ctrlKey ? 'copy' : 'move';
      box.classList.add('drop');
    });
    box.addEventListener('dragleave', () => box.classList.remove('drop'));
    box.addEventListener('drop', (e) => {
      const raw = e.dataTransfer?.getData(ADDR_MIME);
      box.classList.remove('drop');
      if (!raw) return;
      e.preventDefault();
      e.stopPropagation();
      let d: { field: string; name: string; email: string; invalid?: boolean };
      try { d = JSON.parse(raw) as typeof d; } catch { return; }
      if (d.field === fieldId) return; // na stejný řádek
      if (!list.some((a) => a.email === d.email)) list.push({ name: d.name, email: d.email, ...(d.invalid ? { invalid: true } : {}) });
      render(); touch();
      if (d.field.split(':')[0] === composeId && !e.ctrlKey) addrFields.get(d.field)?.(d.email); // v rámci okna přesun
    });
    const render = () => {
      clear(chips);
      for (const a of list) {
        const text = h('span', { class: 'mail-chip-text' }, a.name || a.email);
        const chip = h('span', { class: 'mail-chip' + (a.invalid ? ' invalid' : ''), title: a.invalid ? t('posta.napsat.bad_address', { address: a.email }) : a.email, draggable: 'true' }, text, h('button', { type: 'button', class: 'mail-chip-x', 'aria-label': t('posta.napsat.remove') }, '×'));
        chip.addEventListener('dragstart', (e) => {
          e.dataTransfer?.setData(ADDR_MIME, JSON.stringify({ field: fieldId, name: a.name, email: a.email, invalid: a.invalid }));
          e.dataTransfer?.setData('text/plain', a.name ? `${a.name} <${a.email}>` : a.email); // jinam (pole, jiná aplikace) jako text
          if (e.dataTransfer) e.dataTransfer.effectAllowed = 'copyMove';
        });
        chip.querySelector('button')?.addEventListener('click', () => { list.splice(list.indexOf(a), 1); render(); touch(); });
        text.addEventListener('click', () => { list.splice(list.indexOf(a), 1); render(); input.value = a.name && !a.invalid ? `${a.name} <${a.email}>` : a.email; input.focus(); touch(); }); // klik na čip = upravit
        chips.appendChild(chip);
      }
    };
    const commit = (raw: string): void => {
      for (const part of raw.split(/[;,]/)) {
        const s = part.trim();
        if (!s) continue;
        const m = /^(.*?)\s*<([^>]+)>$/.exec(s);
        const email = (m?.[2] ?? s).trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { // neplatná adresa zůstane (uživatel ji opraví), jen upozornit; odeslat nepůjde
          if (!list.some((a) => a.email === s)) list.push({ name: '', email: s, invalid: true });
          toast(t('posta.napsat.bad_address', { address: s }), 'warn');
          continue;
        }
        if (!list.some((a) => a.email === email)) list.push({ name: (m?.[1] ?? '').replace(/^["']|["']$/g, '').trim(), email });
      }
      render(); touch();
    };
    let menu: IwMenu | null = null;
    const suggest = debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) { menu?.close(); menu = null; return; }
      try {
        const r = await core<{ hits: LookupHit[] }>('lookup', 'search', { source: 'people', q, limit: 8 });
        const items: MenuItem[] = r.hits.filter((x) => (x.fields as { email?: string }).email).map((x) => ({ label: x.label, icon: x.icon ?? 'user', onSelect: () => { const f = x.fields as { name?: string; email?: string }; input.value = ''; if (!list.some((a) => a.email === f.email)) list.push({ name: f.name ?? '', email: String(f.email).toLowerCase() }); render(); touch(); input.focus(); } }));
        menu?.close();
        menu = items.length ? IwMenu.showAt(items, box, { focus: false }) : null; // pole si nechá fokus, nabídku řídí klávesami
      } catch { /* bez našeptávání */ }
    }, 250);
    input.addEventListener('input', () => void suggest());
    input.addEventListener('keydown', (e) => {
      if (menu?.isConnected && menu.handleKey(e)) return; // šipky/Enter/Esc do našeptávače
      if (menu && !menu.isConnected) menu = null;
      if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') { if (input.value.trim()) { e.preventDefault(); commit(input.value); input.value = ''; menu?.close(); } }
      else if (e.key === 'Backspace' && !input.value && list.length) { list.pop(); render(); touch(); }
    });
    input.addEventListener('blur', () => { if (input.value.trim()) { commit(input.value); input.value = ''; } menu?.close(); menu = null; });
    box.addEventListener('click', () => input.focus());
    render();
    return { el: box, row, get: () => [...list], set: (a) => { list.splice(0, list.length, ...a); render(); } };
  }

  // ---- předvyplnění podle režimu
  const o = p.orig;
  const me = new Set(ids.map((i) => i.email.toLowerCase()));
  let initTo: Addr[] = [];
  let initCc: Addr[] = [];
  let initSubject = p.subject;
  if (o && (p.mode === 'reply' || p.mode === 'reply_all')) {
    initTo = [{ name: o.from, email: o.replyTo ?? o.fromEmail }];
    if (p.mode === 'reply_all') {
      initCc = [...o.toList, ...o.ccList].filter((a) => !me.has(a.email.toLowerCase()) && a.email.toLowerCase() !== initTo[0]?.email.toLowerCase());
    }
    initSubject = /^\s*re:/i.test(o.subject) ? o.subject : 'Re: ' + o.subject;
  } else if (o && (p.mode === 'forward' || p.mode === 'forward_attach')) {
    initSubject = /^\s*fwd?:/i.test(o.subject) ? o.subject : 'Fwd: ' + o.subject;
    for (const a of o.attachments) if (a.fileId) atts.push({ fileId: a.fileId, name: a.name, size: a.size, mime: a.mime });
  } else if (p.draft) {
    initTo = p.draft.to; initCc = p.draft.cc; initSubject = p.draft.subject;
    for (const a of p.draft.attachments) if (a.fileId) atts.push({ fileId: a.fileId, name: a.name, size: a.size, mime: a.mime });
  } else if (p.to) {
    initTo = p.to.split(/[;,]/).map((s) => s.trim()).filter(Boolean).map((s) => { const m = /^(.*?)\s*<([^>]+)>$/.exec(s); return { name: (m?.[1] ?? '').trim(), email: (m?.[2] ?? s).toLowerCase() }; });
  }

  const toF = addrField(t('posta.ui.to'), initTo);
  const ccF = addrField(t('posta.ui.cc'), initCc);
  const bccF = addrField(t('posta.napsat.bcc'), []);
  ccF.row.hidden = initCc.length === 0;
  bccF.row.hidden = true;
  const ccToggle = h('button', { type: 'button', class: 'btn ghost sm mail-cc-toggle' }, t('posta.napsat.cc_bcc'));
  ccToggle.addEventListener('click', () => { ccF.row.hidden = false; bccF.row.hidden = false; ccToggle.hidden = true; });

  // ---- Od (identity); alias a poštovní skupina jsou označené, aby nikdo omylem nepsal jménem týmu
  const kindNote = (i: Identity): string => (i.kind === 'own' ? '' : ' · ' + t('posta.napsat.kind_' + i.kind));
  const fromBtn = h('button', { type: 'button', class: 'input color-select-btn mail-from-btn' });
  const renderFrom = () => fromBtn.replaceChildren(h('span', { class: 'cal-dot', style: { background: identity?.color || '#1f6fd0' } }), h('span', { class: 'color-select-label' }, identity ? `${identity.name} <${identity.email}>` + kindNote(identity) + (ids.filter((i) => i.account === identity?.account).length > 1 || ids.length > 1 ? ' · ' + identity.accountName : '') : '—'), icon('chevron', 'icon chevron'));
  fromBtn.addEventListener('click', () => IwMenu.showAt(ids.map((i) => ({ label: `${i.name} <${i.email}>` + kindNote(i) + (i.canSend ? '' : ' (' + t('posta.napsat.no_smtp') + ')'), color: i.color || '#1f6fd0', checked: i.id === identity?.id, disabled: !i.canSend, onSelect: () => { identity = i; renderFrom(); if (!signatureTouched) applySignature(); } })), fromBtn));
  renderFrom();

  // ---- předmět
  const subject = h('input', { class: 'input mail-subject-input', type: 'text', value: initSubject, placeholder: t('posta.ui.subject') }) as HTMLInputElement;
  subject.addEventListener('input', () => { touch(); win.setTitle(subject.value.trim() || t('posta.napsat.title'), 'edit'); });

  // ---- editor
  const editor = h('div', { class: 'mail-editor', contenteditable: 'true', spellcheck: 'true' }) as HTMLDivElement;
  const textarea = h('textarea', { class: 'input mail-editor-text', rows: '14', hidden: true }) as HTMLTextAreaElement;
  const cmd = (name: string, value?: string) => { editor.focus(); document.execCommand(name, false, value); touch(); };
  const tb = (name: string, ic: string, title: string, fn: () => void): HTMLElement => { const b = h('button', { type: 'button', class: 'btn ghost sm', title }, icon(ic, 'icon')); b.addEventListener('mousedown', (e) => e.preventDefault()); b.addEventListener('click', fn); return b; };
  const toolbar = h('div', { class: 'mail-toolbar' },
    tb('bold', 'bold', t('posta.napsat.bold'), () => cmd('bold')), tb('italic', 'italic', t('posta.napsat.italic'), () => cmd('italic')), tb('underline', 'underline', t('posta.napsat.underline'), () => cmd('underline')),
    tb('ul', 'list', t('posta.napsat.list'), () => cmd('insertUnorderedList')), tb('ol', 'list-ordered', t('posta.napsat.list_ordered'), () => cmd('insertOrderedList')),
    tb('link', 'link', t('posta.napsat.link'), async () => { const url = await prompt(t('posta.napsat.link_url'), 'https://', { title: t('posta.napsat.link') }); if (url) cmd('createLink', url); }),
    tb('quote', 'quote', t('posta.napsat.quote'), () => cmd('formatBlock', 'blockquote')), tb('clear', 'eraser', t('posta.napsat.clear_format'), () => cmd('removeFormat')),
  );
  // ---- obrázek do textu (z počítače, ze Souborů, vložením ze schránky) a tabulka
  let savedRange: Range | null = null; // kurzor v editoru před otevřením nabídky / dialogu
  const keepRange = (): void => { const s = window.getSelection(); if (s && s.rangeCount && editor.contains(s.getRangeAt(0).commonAncestorContainer)) savedRange = s.getRangeAt(0).cloneRange(); };
  const restoreRange = (): void => { editor.focus(); if (savedRange) { const s = window.getSelection(); s?.removeAllRanges(); s?.addRange(savedRange); } };
  /** Vložit prvek na místo kurzoru (přes DOM – vložené HTML s atributem style by zablokovala CSP stránky). */
  const insertNode = (node: Node): void => {
    restoreRange();
    const s = window.getSelection();
    const r = s && s.rangeCount && editor.contains(s.getRangeAt(0).commonAncestorContainer) ? s.getRangeAt(0) : null;
    const last = node instanceof DocumentFragment ? node.lastChild : node; // fragment se vložením vyprázdní
    if (r) { r.deleteContents(); r.insertNode(node); if (last) r.setStartAfter(last); r.collapse(true); s?.removeAllRanges(); s?.addRange(r); } else editor.appendChild(node);
    keepRange();
    touch();
  };
  editor.addEventListener('keyup', keepRange);
  editor.addEventListener('mouseup', keepRange);
  editor.addEventListener('input', keepRange);
  /** Obrázek zmenšit na nejvýš 1600 px a rozumnou velikost (fotky JPEG), animovaný GIF nechat. */
  async function imageData(file: Blob): Promise<string> {
    const read = (b: Blob): Promise<string> => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = () => rej(r.error); r.readAsDataURL(b); });
    if (file.type === 'image/gif' || file.size < 150_000) return read(file);
    const bmp = await createImageBitmap(file);
    const k = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const c = document.createElement('canvas');
    c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
    c.getContext('2d')?.drawImage(bmp, 0, 0, c.width, c.height);
    const png = file.type === 'image/png' && file.size < 600_000;
    return c.toDataURL(png ? 'image/png' : 'image/jpeg', 0.85);
  }
  async function insertImage(file: Blob, name: string): Promise<void> {
    if (!file.type.startsWith('image/')) { toast(t('posta.napsat.not_image'), 'error'); return; }
    try {
      const img = document.createElement('img');
      img.src = await imageData(file);
      img.alt = name;
      img.style.maxWidth = '100%'; // přes CSSOM (CSP); v odeslaném HTML zůstane jako style
      insertNode(img);
    } catch { toast(t('posta.napsat.image_failed'), 'error'); }
  }
  const imgInput = h('input', { type: 'file', accept: 'image/*', hidden: true }) as HTMLInputElement;
  imgInput.addEventListener('change', () => { const f = imgInput.files?.[0]; if (f) void insertImage(f, f.name); imgInput.value = ''; });
  const imageMenu = (): MenuItem[] => [
    { label: t('posta.napsat.image_pc'), icon: 'upload', onSelect: () => imgInput.click() },
    { label: t('posta.napsat.image_files'), icon: 'folder', onSelect: async () => {
      const items = await pickFile({ mode: 'storage', multiple: false, parent: win });
      const it = items?.[0];
      if (!it?.url) return;
      try { const blob = await (await fetch(bootPrefix + it.url)).blob(); await insertImage(blob, it.name); } catch { toast(t('posta.napsat.image_failed'), 'error'); }
    } },
  ];
  const imgBtn = tb('image', 'image', t('posta.napsat.image'), () => { keepRange(); IwMenu.showAt(imageMenu(), imgBtn); });
  // klik na obrázek v textu: velikost / odebrat
  editor.addEventListener('click', (e) => {
    const img = (e.target as HTMLElement).closest('img');
    if (!img || !editor.contains(img)) return;
    const size = (w: number | null): void => { if (w) img.setAttribute('width', String(w)); else img.removeAttribute('width'); touch(); };
    IwMenu.showAt([
      { label: t('posta.napsat.img_small'), checked: img.getAttribute('width') === '200', onSelect: () => size(200) },
      { label: t('posta.napsat.img_medium'), checked: img.getAttribute('width') === '400', onSelect: () => size(400) },
      { label: t('posta.napsat.img_large'), checked: img.getAttribute('width') === '640', onSelect: () => size(640) },
      { label: t('posta.napsat.img_original'), checked: !img.getAttribute('width'), onSelect: () => size(null) },
      { separator: true, label: '' },
      { label: t('posta.napsat.img_remove'), icon: 'trash', danger: true, onSelect: () => { img.remove(); touch(); } },
    ], img);
  });
  async function insertTable(): Promise<void> {
    keepRange();
    const rows = h('input', { class: 'input', type: 'number', min: '1', max: '50', value: '3' }) as HTMLInputElement;
    const cols = h('input', { class: 'input', type: 'number', min: '1', max: '12', value: '3' }) as HTMLInputElement;
    const r = await dialog({ title: t('posta.napsat.table'), icon: 'table', message: h('div', { class: 'mail-table-dlg' }, h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('posta.napsat.table_rows')), rows), h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('posta.napsat.table_cols')), cols)), buttons: [{ id: 'cancel', label: t('core.ui.cancel') }, { id: 'ok', label: t('posta.napsat.table_insert'), primary: true }] });
    if (r.id !== 'ok') return;
    const nr = Math.max(1, Math.min(50, Number(rows.value) || 3));
    const nc = Math.max(1, Math.min(12, Number(cols.value) || 3));
    // styly přímo v prvcích (poštovní programy neznají naše CSS), nastavené přes CSSOM kvůli CSP
    const table = document.createElement('table');
    table.style.cssText = 'border-collapse:collapse;margin:6px 0';
    for (let i = 0; i < nr; i++) {
      const tr = table.insertRow();
      for (let j = 0; j < nc; j++) { const td = tr.insertCell(); td.style.cssText = 'border:1px solid #c8c4bb;padding:4px 8px;min-width:40px'; td.innerHTML = '&nbsp;'; }
    }
    const frag = document.createDocumentFragment();
    const after = document.createElement('p');
    after.appendChild(document.createElement('br'));
    frag.append(table, after);
    insertNode(frag);
  }
  const tableBtn = tb('table', 'table', t('posta.napsat.table'), () => void insertTable());
  toolbar.append(imgBtn, tableBtn, imgInput);
  // tabulka: pravé tlačítko v buňce – řádek / sloupec přidat a odebrat
  contextMenu(editor, (e) => {
    const cell = (e.target as HTMLElement).closest('td');
    const row = cell?.parentElement as HTMLTableRowElement | null;
    const table = cell?.closest('table');
    if (!cell || !row || !table || !editor.contains(table)) return null;
    const ci = Array.from(row.cells).indexOf(cell as HTMLTableCellElement);
    const blank = (): HTMLTableCellElement => { const c = (cell as HTMLTableCellElement).cloneNode(false) as HTMLTableCellElement; c.innerHTML = '&nbsp;'; return c; };
    const done = (): void => { touch(); };
    return [
      { label: t('posta.napsat.row_add'), icon: 'plus', onSelect: () => { const n = row.cloneNode(false) as HTMLTableRowElement; for (let i = 0; i < row.cells.length; i++) n.appendChild(blank()); row.after(n); done(); } },
      { label: t('posta.napsat.col_add'), icon: 'plus', onSelect: () => { for (const r of Array.from(table.rows)) r.cells[ci]?.after(blank()); done(); } },
      { separator: true, label: '' },
      { label: t('posta.napsat.row_remove'), icon: 'x', onSelect: () => { if (table.rows.length > 1) row.remove(); else table.remove(); done(); } },
      { label: t('posta.napsat.col_remove'), icon: 'x', onSelect: () => { if (row.cells.length > 1) { for (const r of Array.from(table.rows)) r.cells[ci]?.remove(); } else table.remove(); done(); } },
      { label: t('posta.napsat.table_remove'), icon: 'trash', danger: true, onSelect: () => { table.remove(); done(); } },
    ];
  });
  // koncepce A (vzor záznamu Kontakty): Odeslat · Přiložit ▾ · Podpis ▾ · ⋯ (Uložit koncept, Prostý text / HTML, Zahodit) v záhlaví;
  // telefon ← · předmět · 📎 · ➤ · ⋮. Klasické ovládání: tlačítka dole jako dosud.
  const bar = useBar() ? new IwCommandBar() : null;
  const fmtToggle = h('button', { type: 'button', class: 'btn ghost sm', onClick: () => setHtml(!html) }, t(html ? 'posta.napsat.to_text' : 'posta.napsat.to_html'));
  if (!bar) toolbar.append(h('span', { class: 'mail-toolbar-spacer' }), fmtToggle);
  editor.addEventListener('input', () => touch());
  textarea.addEventListener('input', () => touch());
  editor.addEventListener('paste', (e) => { // obrázek ze schránky do textu; jinak vkládat jako prostý text (bez cizích stylů)
    const img = Array.from(e.clipboardData?.files ?? []).find((f) => f.type.startsWith('image/'));
    if (img) { e.preventDefault(); keepRange(); void insertImage(img, img.name || 'obrazek'); return; }
    const text = e.clipboardData?.getData('text/plain');
    if (text) { e.preventDefault(); document.execCommand('insertText', false, text); }
  });

  /**
   * HTML do editoru (koncept, citace, předvyplnění): rozebrat mimo stránku a atributy style nastavit přes CSSOM –
   * vložení přes innerHTML by CSP stránky inline styly zablokovalo (tabulka, velikost obrázku). Odeslané HTML je má dál.
   */
  function htmlNodes(markup: string): Node[] {
    const doc = new DOMParser().parseFromString('<body>' + markup + '</body>', 'text/html');
    const css = new Map<Element, string>();
    doc.body.querySelectorAll('[style]').forEach((el) => { css.set(el, el.getAttribute('style') ?? ''); el.removeAttribute('style'); });
    const src = Array.from(doc.body.querySelectorAll('*'));
    const nodes = Array.from(doc.body.childNodes).map((n) => document.importNode(n, true));
    // styly až v dokumentu stránky (CSSOM) – stejné pořadí prvků jako ve zdroji
    const holder = document.createElement('div');
    holder.append(...nodes);
    Array.from(holder.querySelectorAll('*')).forEach((el, i) => { const c = css.get(src[i] as Element); if (c) (el as HTMLElement).style.cssText = c; });
    return Array.from(holder.childNodes);
  }
  function setEditorHtml(markup: string): void {
    editor.replaceChildren(...htmlNodes(markup));
  }
  function quoteHtml(): string {
    if (!o || !p.prefs.quote || p.mode === 'forward_attach') return ''; // celá zpráva je v příloze, text se necituje
    const when = o.date ? fmtDate(new Date(o.date.replace(' ', 'T') + 'Z')) + ' ' + fmtTime(new Date(o.date.replace(' ', 'T') + 'Z')) : '';
    const head = p.mode === 'forward'
      ? `<div>-------- ${esc(t('posta.napsat.forwarded'))} --------<br>${esc(t('posta.ui.from'))}: ${esc(o.from)} &lt;${esc(o.fromEmail)}&gt;<br>${esc(t('posta.ui.date'))}: ${esc(when)}<br>${esc(t('posta.ui.subject'))}: ${esc(o.subject)}<br>${esc(t('posta.ui.to'))}: ${esc(o.toList.map((a) => a.email).join(', '))}</div><br>`
      : `<div>${esc(t('posta.napsat.wrote', { date: when, name: o.from }))}</div>`;
    const body = o.html ?? `<pre style="white-space:pre-wrap;font:inherit">${esc(o.text)}</pre>`;
    return `<br><br>${head}<blockquote style="border-left:3px solid #ccc;margin:6px 0;padding-left:10px;color:#555">${body}</blockquote>`;
  }
  function quoteText(): string {
    if (!o || !p.prefs.quote || p.mode === 'forward_attach') return '';
    const when = o.date ? fmtDate(new Date(o.date.replace(' ', 'T') + 'Z')) + ' ' + fmtTime(new Date(o.date.replace(' ', 'T') + 'Z')) : '';
    const head = p.mode === 'forward' ? `-------- ${t('posta.napsat.forwarded')} --------\n${t('posta.ui.from')}: ${o.from} <${o.fromEmail}>\n${t('posta.ui.date')}: ${when}\n${t('posta.ui.subject')}: ${o.subject}\n` : t('posta.napsat.wrote', { date: when, name: o.from });
    return '\n\n' + head + '\n' + o.text.split('\n').map((l) => '> ' + l).join('\n');
  }
  function signatureHtml(): string { const s = identity?.signature; if (signatureChoice === 'none' || !s) return ''; return `<div class="mail-sig">-- <br>${s.html}</div>`; }
  function signatureText(): string { const s = identity?.signature; if (signatureChoice === 'none' || !s) return ''; return `\n\n-- \n${s.text}`; }
  function applySignature(): void {
    if (html) {
      editor.querySelectorAll('.mail-sig').forEach((e) => e.remove());
      const sig = signatureHtml();
      if (!sig) return;
      const q = editor.querySelector('blockquote');
      if (p.prefs.signaturePos === 'below' || !q) editor.append(...htmlNodes(sig));
      else q.before(...htmlNodes(sig));
    }
  }
  function initialBody(): void {
    if (p.draft) { // pokračování v konceptu: obsah beze změn (podpis je už uvnitř)
      if (p.draft.html) { if (!html) setHtml(true); setEditorHtml(p.draft.html); } else { if (html) setHtml(false); textarea.value = p.draft.text; }
      dirty = false;
      return;
    }
    if (html) {
      setEditorHtml((p.body ? esc(p.body).replace(/\n/g, '<br>') : '<div><br></div>') + quoteHtml());
      applySignature();
    } else {
      textarea.value = (p.body || '') + signatureText() + quoteText();
    }
  }
  function setHtml(on: boolean): void {
    if (on === html) return;
    if (on) { editor.innerHTML = esc(textarea.value).replace(/\n/g, '<br>'); } else { textarea.value = editor.innerText; }
    html = on;
    editor.hidden = !html; textarea.hidden = html;
    toolbar.querySelectorAll('button').forEach((b) => { if (b !== fmtToggle) (b as HTMLButtonElement).disabled = !html; });
    fmtToggle.textContent = t(html ? 'posta.napsat.to_text' : 'posta.napsat.to_html');
    toolbar.hidden = Boolean(bar) && !html; // nové ovládání: formátování jen u HTML (přepínač je v ⋯)
    touch();
    refreshBar();
  }

  // ---- přílohy
  const attList = h('div', { class: 'mail-compose-atts' });
  function renderAtts(): void {
    clear(attList);
    for (const a of atts) {
      const chip = h('span', { class: 'mail-att' }, icon('file', 'icon'), h('span', null, a.name), h('span', { class: 'muted small' }, ' ' + fmtSize(a.size)), h('button', { type: 'button', class: 'mail-chip-x', 'aria-label': t('posta.napsat.remove') }, '×'));
      chip.querySelector('button')?.addEventListener('click', () => { atts.splice(atts.indexOf(a), 1); renderAtts(); touch(); });
      attList.appendChild(chip);
    }
  }
  // ---- chytrá příloha: místo souboru odkaz na něj (soubor zůstává v Souborech, příjemce ho stáhne z /s/…; platnost,
  // volitelné heslo; u odeslané zprávy je vidět, kdy a odkud se stahovalo). Velký soubor z počítače se tak nabídne sám.
  const links: string[] = [];
  const LINK_OVER = 20 * 1024 * 1024;
  async function linkDialog(names: string[]): Promise<{ days: number; password: string } | null> {
    const o = await linkOptionsDialog(names, false); // stejný dialog jako Soubory, podle pravidel správce
    return o ? { days: o.days, password: o.password } : null;
  }
  async function insertLinks(items: StorageItem[], o: { days: number; password: string }): Promise<void> {
    for (const it of items) {
      try {
        const r = await core<{ link: { id: string; path: string; name: string; size: number; expiresAt: string | null } }>('links', 'create', { id: it.id, days: o.days, password: o.password || null, context: { app: 'posta' } });
        links.push(r.link.id);
        const p = document.createElement('p');
        p.className = 'mail-link';
        const a = document.createElement('a');
        a.href = location.origin + bootPrefix + r.link.path;
        a.textContent = r.link.name;
        const until = r.link.expiresAt ? ' · ' + t('posta.link.until', { date: fmtDate(new Date(r.link.expiresAt.replace(' ', 'T') + 'Z')) }) : '';
        p.append('📎 ', a, ` (${fmtSize(r.link.size)}${until}${o.password ? ' · ' + t('posta.link.protected') : ''})`);
        insertNode(p);
      } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
    }
    if (o.password) toast(t('posta.link.password_note'), 'info');
  }
  async function linkFromFiles(): Promise<void> {
    keepRange();
    const items = (await pickFile({ mode: 'storage', multiple: true, parent: win }))?.filter((x) => x.kind === 'file') ?? [];
    if (!items.length) return;
    const o = await linkDialog(items.map((x) => x.name));
    if (o) await insertLinks(items, o);
  }
  async function addFromComputer(files: FileList | File[]): Promise<void> {
    const all = Array.from(files);
    const big = all.filter((f) => f.size > LINK_OVER);
    if (big.length) { // velký soubor raději jako odkaz (příjemcům se nezahltí schránka, server ho nemusí odmítnout)
      keepRange();
      const o = await linkDialog(big.map((f) => f.name));
      if (o) {
        try {
          const folder = (await core<{ item: StorageItem }>('storage', 'folder', { name: t('posta.link.folder') })).item;
          const up = (await upload(folder.id, big)) as { items?: StorageItem[] } | StorageItem[];
          await insertLinks(Array.isArray(up) ? up : (up.items ?? []), o);
        } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
        files = all.filter((f) => f.size <= LINK_OVER);
        if (!files.length) return;
      }
    }
    try {
      const r = (await upload('', files, undefined, true)) as { items?: StorageItem[] } | StorageItem[];
      const items = Array.isArray(r) ? r : (r.items ?? []);
      for (const it of items) atts.push({ fileId: it.id, name: it.name, size: it.size ?? 0, mime: it.mime ?? '' });
      renderAtts(); touch();
    } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  const fileInput = h('input', { type: 'file', multiple: 'multiple', hidden: true }) as HTMLInputElement;
  fileInput.addEventListener('change', () => { if (fileInput.files?.length) void addFromComputer(fileInput.files); fileInput.value = ''; });
  const attBtn = h('button', { type: 'button', class: 'btn ghost sm' }, icon('paperclip', 'icon'), ' ', t('posta.napsat.attach'));
  const attachMenu = (): MenuItem[] => [
    { label: t('posta.napsat.from_computer'), icon: 'upload', onSelect: () => fileInput.click() },
    { label: t('posta.napsat.from_files'), icon: 'folder', onSelect: async () => { const items = await pickFile({ mode: 'storage', multiple: true, parent: win }); for (const it of items ?? []) atts.push({ fileId: it.id, name: it.name, size: it.size ?? 0, mime: it.mime ?? '' }); renderAtts(); touch(); } },
    { separator: true, label: '' },
    { label: t('posta.link.menu'), icon: 'link', onSelect: () => void linkFromFiles() },
  ];
  attBtn.addEventListener('click', () => IwMenu.showAt(attachMenu(), attBtn));
  renderAtts();

  // ---- podpis volba
  const sigBtn = h('button', { type: 'button', class: 'btn ghost sm' }, icon('edit', 'icon'), ' ', t('posta.napsat.signature'));
  const sigMenu = (): MenuItem[] => [
    { label: t('posta.napsat.sig_default'), checked: signatureChoice === 'default', onSelect: () => { signatureChoice = 'default'; signatureTouched = true; applySignature(); refreshBar(); } },
    { label: t('posta.napsat.sig_none'), checked: signatureChoice === 'none', onSelect: () => { signatureChoice = 'none'; signatureTouched = true; applySignature(); refreshBar(); } },
  ];
  sigBtn.addEventListener('click', () => IwMenu.showAt(sigMenu(), sigBtn));

  // ---- odeslání
  const sendBtn = h('button', { type: 'button', class: 'btn primary' }, icon('send', 'icon'), ' ', t('posta.napsat.send'));
  const status = h('div', { class: 'mail-send-status', hidden: true });
  // priorita (X-Priority / Importance) a žádost o potvrzení přečtení – server je umí, okno je nastaví v ⋯ / ▾
  let priority: 'normal' | 'high' | 'low' = 'normal';
  let receipt = false;
  const prioBadge = h('span', { class: 'mail-prio-badge', hidden: true });
  const renderPrio = (): void => {
    prioBadge.hidden = priority === 'normal';
    prioBadge.replaceChildren(icon(priority === 'high' ? 'alert' : 'chevron-down', 'icon'), t('posta.napsat.prio_' + priority));
  };
  const setPriority = (v: 'normal' | 'high' | 'low'): void => { priority = v; renderPrio(); touch(); refreshBar(); };
  const prioMenu = (): MenuItem[] => (['high', 'normal', 'low'] as const).map((v) => ({ label: t('posta.napsat.prio_' + v), icon: v === 'high' ? 'alert' : undefined, checked: priority === v, onSelect: () => setPriority(v) }));
  let dirty = false;
  const touch = () => { dirty = true; saveDraftSoon(); };
  async function send(): Promise<void> {
    const to = toF.get(); const cc = ccF.get(); const bcc = bccF.get();
    if (!identity) { toast(t('posta.napsat.no_identity'), 'error'); return; }
    if (to.length + cc.length + bcc.length === 0) { toast(t('posta.napsat.no_recipient'), 'error'); toF.el.querySelector('input')?.focus(); return; }
    const bad = [...to, ...cc, ...bcc].find((a) => a.invalid);
    if (bad) { toast(t('posta.napsat.bad_address', { address: bad.email }), 'error'); (to.includes(bad) ? toF : cc.includes(bad) ? ccF : bccF).el.querySelector('input')?.focus(); return; }
    if (!subject.value.trim() && !(await confirm(t('posta.napsat.no_subject_confirm')))) return;
    setSending(true);
    try {
      const r = await action<{ id: string; sendAt: string; delay: number; held?: boolean; notice?: string | null }>(desc.app, desc.window, 'send', desc.params, {
        identity: identity.id, to, cc, bcc, subject: subject.value.trim(), html: html ? editor.innerHTML : null, text: html ? editor.innerText : textarea.value,
        attachments: atts.map((a) => ({ fileId: a.fileId, name: a.name })), inReplyTo: o?.id ?? null, mode: p.mode, delay: p.prefs.sendDelay, draft: draftMessage, priority, receipt, links,
      });
      sentId = r.id;
      dirty = false;
      if (r.notice) toast(r.notice, r.held ? 'error' : 'info'); // limity odesílání: odloženo / zadrženo
      void core('drafts', 'delete', { key: draftKey }).catch(() => undefined);
      if (r.delay > 0) {
        let left = r.delay;
        status.hidden = false;
        const undoBtn = h('button', { type: 'button', class: 'btn sm' }, icon('undo', 'icon'), ' ', t('posta.napsat.undo_send'));
        const txt = h('span', null, t('posta.napsat.sending_in', { s: left }));
        status.replaceChildren(icon('check', 'icon'), ' ', txt, ' ', undoBtn);
        form.classList.add('sending');
        const timer = window.setInterval(() => { left--; txt.textContent = t('posta.napsat.sending_in', { s: left }); if (left <= 0) { window.clearInterval(timer); toast(t('posta.napsat.sent'), 'ok'); win.close(); } }, 1000);
        undoBtn.addEventListener('click', async () => {
          window.clearInterval(timer);
          try { const c = await action<{ cancelled: boolean }>(desc.app, desc.window, 'cancel', desc.params, { id: r.id }); if (c.cancelled) { sentId = null; status.hidden = true; form.classList.remove('sending'); setSending(false); toast(t('posta.napsat.cancelled'), 'ok'); } else toast(t('posta.napsat.too_late'), 'error'); } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
        });
      } else {
        toast(t('posta.napsat.sent'), 'ok');
        win.close();
      }
    } catch (e) {
      setSending(false);
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }
  sendBtn.addEventListener('click', () => void send());
  let sending = false;
  function setSending(on: boolean): void {
    sending = on;
    sendBtn.disabled = on;
    refreshBar();
  }

  // ---- koncept (jádro: drafts.save/load/delete)
  const draftState = () => ({ to: toF.get(), cc: ccF.get(), bcc: bccF.get(), subject: subject.value, html, body: html ? editor.innerHTML : textarea.value, atts, identity: identity?.id });
  let savingDraft = false;
  /** Koncept do jádra (obnova okna) a do složky Koncepty na IMAP (vidí ho i telefon; předchozí verze se nahradí). */
  async function saveDraft(): Promise<void> {
    if (sentId || savingDraft) return;
    savingDraft = true;
    try {
      await core('drafts', 'save', { key: draftKey, title: subject.value.trim() || t('posta.napsat.title'), state: draftState() }).catch(() => undefined);
      if (identity && (toF.get().length || ccF.get().length || bccF.get().length || subject.value.trim() || (html ? editor.innerText : textarea.value).trim())) {
        const r = await action<{ draftMessage: string | null }>(desc.app, desc.window, 'draft', desc.params, {
          identity: identity.id, to: toF.get().filter((a) => !a.invalid), cc: ccF.get().filter((a) => !a.invalid), bcc: bccF.get().filter((a) => !a.invalid), subject: subject.value.trim(),
          html: html ? editor.innerHTML : null, text: html ? editor.innerText : textarea.value, attachments: atts.map((a) => ({ fileId: a.fileId, name: a.name })), inReplyTo: o?.id ?? null, mode: p.mode, draft: draftMessage, priority, receipt,
        }).catch(() => null);
        if (r?.draftMessage) draftMessage = r.draftMessage;
      }
    } finally { savingDraft = false; }
  }
  const saveDraftSoon = debounce(() => { if (dirty) void saveDraft(); }, 30000);
  async function discardDraft(): Promise<void> {
    dirty = false;
    await core('drafts', 'delete', { key: draftKey }).catch(() => undefined);
    if (draftMessage) { const id = draftMessage; draftMessage = null; await action(desc.app, desc.window, 'discard', desc.params, { id }).catch(() => undefined); }
  }
  const discardBtn = h('button', { type: 'button', class: 'btn ghost sm' }, t('posta.napsat.discard'));
  discardBtn.addEventListener('click', () => { void discardDraft(); win.close(); });
  async function saveDraftNow(): Promise<void> {
    dirty = false;
    await saveDraft();
    toast(t('posta.napsat.draft_saved'), 'ok');
  }
  /** Lišta v záhlaví (nové ovládání). */
  function refreshBar(): void {
    if (!bar) return;
    const items: BarItem[] = [
      { id: 'send', label: t('posta.napsat.send'), icon: 'send', shortcut: 'Ctrl+Enter', place: 'primary', disabled: sending, onClick: () => void send() },
      { id: 'attach', label: t('posta.napsat.attach'), icon: 'paperclip', place: 'action', menu: attachMenu() },
      { id: 'signature', label: t('posta.napsat.signature'), icon: 'edit', place: 'action', menu: sigMenu() },
      { id: 'save_draft', label: t('posta.napsat.save_draft'), icon: 'save', shortcut: 'Ctrl+S', place: 'more', onClick: () => void saveDraftNow() },
      { id: 'format', label: t(html ? 'posta.napsat.to_text' : 'posta.napsat.to_html'), icon: 'text', place: 'more', onClick: () => setHtml(!html) },
      { id: 'priority', label: t('posta.napsat.priority'), icon: 'alert', place: 'more', menu: prioMenu() },
      { id: 'receipt', label: t('posta.napsat.receipt'), icon: 'check', place: 'more', toggle: true, active: receipt, onClick: () => { receipt = !receipt; touch(); refreshBar(); } },
      { id: 'discard', label: t('posta.napsat.discard'), icon: 'trash', shortcut: 'Esc', danger: true, place: 'action', onClick: () => void win.close() }, // zeptá se, rozepsané lze uložit
    ];
    bar.setItems(items);
    const attachIcon = headButton('paperclip', t('posta.napsat.attach'), (b) => IwMenu.showAt(attachMenu(), b));
    renderBarHeader({ win, wm, bar, back: true, mobile: [attachIcon] });
  }

  // ---- kostra
  const form = h('div', { class: 'view mail-compose' },
    h('div', { class: 'mail-compose-head' },
      h('div', { class: 'mail-compose-row' }, h('span', { class: 'mail-compose-label' }, t('posta.napsat.from')), fromBtn),
      h('div', { class: 'mail-compose-row-wrap' }, toF.row, ccToggle),
      ccF.row, bccF.row,
      h('label', { class: 'mail-compose-row' }, h('span', { class: 'mail-compose-label' }, t('posta.ui.subject')), subject, prioBadge),
    ),
    toolbar,
    h('div', { class: 'mail-editor-wrap' }, editor, textarea),
    attList,
    bar ? h('div', { class: 'mail-compose-foot', hidden: true }, fileInput) : h('div', { class: 'mail-compose-foot' }, sendBtn, attBtn, sigBtn, h('span', { class: 'mail-toolbar-spacer' }), discardBtn, fileInput),
    status,
  );
  if (bar) form.classList.add('chrome-bar');
  async function addFromStorage(ids: string[]): Promise<void> {
    const r = await core<{ items: StorageItem[] }>('storage', 'items', { ids });
    for (const it of r.items) if (it.kind === 'file' && !atts.some((a) => a.fileId === it.id)) atts.push({ fileId: it.id, name: it.name, size: it.size ?? 0, mime: it.mime ?? '' });
    renderAtts(); touch();
  }
  form.addEventListener('dragover', (e) => { if (e.dataTransfer?.types.includes('Files') || e.dataTransfer?.types.includes('application/x-iwan-items')) { e.preventDefault(); form.classList.add('drop'); } });
  form.addEventListener('dragleave', () => form.classList.remove('drop'));
  form.addEventListener('drop', (e) => {
    form.classList.remove('drop');
    const items = e.dataTransfer?.getData('application/x-iwan-items');
    if (items) { e.preventDefault(); void addFromStorage(JSON.parse(items) as string[]).catch((er: ApiError) => toast(tk(er.message), 'error')); return; } // ze Souborů
    if (e.dataTransfer?.files.length) { e.preventDefault(); void addFromComputer(e.dataTransfer.files); }
  });
  editor.hidden = !html; textarea.hidden = html;
  toolbar.hidden = Boolean(bar) && !html;
  initialBody();
  refreshBar();
  type DraftState = { to?: Addr[]; cc?: Addr[]; bcc?: Addr[]; subject?: string; html?: boolean; body?: string; atts?: Att[]; identity?: string };
  function applyDraft(s: DraftState): void {
    toF.set(s.to ?? []); ccF.set(s.cc ?? []); bccF.set(s.bcc ?? []);
    if (s.cc?.length || s.bcc?.length) { ccF.row.hidden = false; bccF.row.hidden = false; ccToggle.hidden = true; }
    subject.value = s.subject ?? '';
    win.setTitle(subject.value.trim() || t('posta.napsat.title'), 'edit');
    if (s.html !== undefined && s.html !== html) setHtml(s.html);
    if (html) setEditorHtml(s.body ?? ''); else textarea.value = s.body ?? '';
    atts.splice(0, atts.length, ...(s.atts ?? [])); renderAtts();
    const id = ids.find((i) => i.id === s.identity); if (id) { identity = id; renderFrom(); }
  }
  const loadDraft = (key: string) => core<{ state: DraftState | null }>('drafts', 'load', { key }).then((d) => d?.state ?? null);
  let restored = false; // okno obnovené po znovunačtení (setState s vlastním konceptem) – nic nenabízet
  if (!isNew) { // odpověď na tutéž zprávu: koncept patří k ní, obnoví se sám
    void loadDraft(draftKey).then((s) => { if (s && !sentId) { applyDraft(s); toast(t('posta.napsat.draft_restored'), 'info'); } }).catch(() => undefined);
  } else {
    void core<{ drafts: { key: string; title: string; updated_at: string }[] }>('drafts', 'list').then((r) => {
      const d = r.drafts.find((x) => (x.key === 'posta/napsat/new' || x.key.startsWith('posta/napsat/new:')) && !openDraftKeys.has(x.key));
      if (!d || restored || dirty || sentId) return;
      const when = fmtDate(new Date(d.updated_at.replace(' ', 'T') + 'Z')) + ' ' + fmtTime(new Date(d.updated_at.replace(' ', 'T') + 'Z'));
      const go = h('button', { type: 'button', class: 'btn sm primary' }, t('posta.napsat.draft_continue'));
      const drop = h('button', { type: 'button', class: 'btn sm' }, t('posta.napsat.discard'));
      const bar2 = h('div', { class: 'mail-banner mail-draft-offer' }, icon('edit', 'icon'), h('span', null, t('posta.napsat.draft_offer', { title: d.title, when })), go, drop);
      go.addEventListener('click', () => void loadDraft(d.key).then((s) => {
        if (!s) return;
        openDraftKeys.delete(draftKey); draftKey = d.key; openDraftKeys.add(draftKey); // pokračovat ve stejném konceptu
        applyDraft(s); bar2.remove();
      }));
      drop.addEventListener('click', () => { void core('drafts', 'delete', { key: d.key }).catch(() => undefined); bar2.remove(); });
      form.querySelector('.mail-compose-head')?.after(bar2);
    }).catch(() => undefined);
  }
  win.setTitle(initSubject || t('posta.napsat.title'), 'edit');

  return {
    el: form,
    setMode: () => refreshBar(),
    shortcut: (sc: string): boolean => {
      if (!bar) return false;
      if (sc === 'Ctrl+Enter') { void send(); return true; }
      if (sc === 'Ctrl+S') { void saveDraftNow(); return true; }
      if (sc === 'Esc') { void win.close(); return true; } // jako Zahodit změny ve vzoru – s dotazem, je-li co ztratit
      return false;
    },
    getState: () => ({ draftKey }), // po znovunačtení stránky okno pokračuje ve svém konceptu
    setState: (s) => {
      const st = s as { prefill?: { to?: string; subject?: string; body?: string }; draftKey?: string } | null;
      if (isNew && st?.draftKey && st.draftKey.startsWith('posta/napsat/new:')) {
        restored = true;
        form.querySelector('.mail-draft-offer')?.remove();
        openDraftKeys.delete(draftKey); draftKey = st.draftKey; openDraftKeys.add(draftKey);
        void loadDraft(draftKey).then((d) => { if (d) applyDraft(d); }).catch(() => undefined);
      }
      // předvyplnění asistentem (restore.prefill: to, subject, body)
      const pf = st?.prefill;
      if (!pf) return;
      if (pf.to) { toF.set(pf.to.split(/[;,]/).map((x) => x.trim()).filter(Boolean).map((x) => ({ name: '', email: x.toLowerCase() }))); }
      if (pf.subject) { subject.value = pf.subject; win.setTitle(pf.subject, 'edit'); }
      if (pf.body) { if (html) setEditorHtml(esc(pf.body).replace(/\n/g, '<br>') + signatureHtml()); else textarea.value = pf.body + signatureText(); }
      touch();
    },
    focus: () => { if (!toF.get().length) toF.el.querySelector('input')?.focus(); else if (html) editor.focus(); else textarea.focus(); },
    canClose: async () => {
      if (!dirty || sentId !== null) return true;
      const r = await dialog({ title: t('posta.napsat.close_title'), message: t('posta.napsat.close_confirm'), buttons: [
        { id: 'cancel', label: t('posta.napsat.close_back') },
        { id: 'discard', label: t('posta.napsat.discard'), danger: true },
        { id: 'save', label: t('posta.napsat.close_save'), primary: true },
      ] });
      if (r.id === 'cancel') return false;
      if (r.id === 'discard') { void discardDraft(); return true; }
      await saveDraftNow();
      return true;
    },
    destroy: () => { openDraftKeys.delete(draftKey); if (!sentId && links.length) for (const id of links) void core('links', 'revoke', { id }).catch(() => undefined); /* veřejné odkazy neodeslané zprávy nesmí zůstat */ if (dirty && !sentId) void saveDraft(); for (const k of Array.from(addrFields.keys())) if (k.startsWith(composeId + ':')) addrFields.delete(k); },
  };
});

export {};
